import type { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { Botao } from '../../componentes/base';
import { BuscaRemota, CampoControlado, Entrada, Rotulo } from '../../componentes/formulario';
import { get } from '../../nucleo/api';
import { dinheiro } from '../../nucleo/formato';
import { novaLinha, numeroDe, type LinhaItem, type ResumoPreco } from './tipos';
import { anunciarOcultos } from '../../componentes/sensivel';

/** Total da linha para mostrar na tela; o servidor é quem arredonda de verdade (para o par). */
export const totalDaLinha = (l: LinhaItem) => Math.round((numeroDe(l.quantidade) ?? 0) * (l.preco ?? 0));
export const totalDasLinhas = (ls: LinhaItem[]) => ls.reduce((a, l) => a + totalDaLinha(l), 0);

export interface OpcoesApi {
  preco: boolean;
  prazo: boolean;
}

/** Itens no formato da API. Lança Error com a mensagem pronta quando uma linha está incompleta. */
export function linhasParaApi(linhas: LinhaItem[], o: OpcoesApi, msg: { semItens: string; incompleto: string }) {
  const validas = linhas.filter((l) => l.produto_id || l.preco);
  if (!validas.length) throw new Error(msg.semItens);
  return validas.map((l) => {
    const q = numeroDe(l.quantidade);
    if (!l.produto_id || !q || q <= 0 || (o.preco && l.preco === null)) throw new Error(msg.incompleto);
    return {
      produto_id: l.produto_id,
      quantidade: q,
      ...(o.preco ? { preco_unitario_centavos: l.preco as number } : {}),
      ...(o.prazo ? { prazo_dias: l.prazo.trim() ? Number(l.prazo) : null } : {}),
    };
  });
}

/**
 * Editor de linhas por produto (requisição, cotação e pedido). O produto vem do Estoque; com o fornecedor conhecido,
 * escolher o produto já sugere o último preço pago a ele (M10.4) ou, sem histórico, o custo do Estoque.
 */
export function ItensEditor({
  linhas,
  aoMudar,
  comPreco,
  comPrazo,
  fornecedorId,
  moeda,
  produtosFixos,
}: {
  linhas: LinhaItem[];
  aoMudar: Dispatch<SetStateAction<LinhaItem[]>>;
  comPreco?: boolean;
  comPrazo?: boolean;
  fornecedorId?: string | null;
  moeda: string;
  /** Os produtos vêm prontos (cotação de uma requisição): só quantidade, preço e prazo mudam. */
  produtosFixos?: boolean;
}) {
  const { t } = useTranslation('compras');
  const muda = (chave: string, parcial: Partial<LinhaItem>) => aoMudar((ls) => ls.map((l) => (l.chave === chave ? { ...l, ...parcial } : l)));

  async function escolherProduto(chave: string, id: string | null, item?: Record<string, unknown>) {
    if (!id || !item) return muda(chave, { produto_id: '', produto_rotulo: '', dica: '' });
    muda(chave, { produto_id: id, produto_rotulo: `${String(item.codigo)} ${String(item.nome)}` });
    if (!comPreco) return;
    const custo = Number(item.custo_centavos ?? 0);
    let preco = custo;
    let dica = custo > 0 ? t('itens.dicaCusto', { valor: dinheiro(custo, moeda) }) : '';
    if (fornecedorId) {
      try {
        const r = await get<ResumoPreco[]>('/compras/precos/resumo', { produto_id: id, fornecedor_id: fornecedorId });
        if (r[0]) {
          preco = r[0].ultimo_centavos;
          dica = t('itens.dicaUltimo', { valor: dinheiro(r[0].ultimo_centavos, moeda) });
        }
      } catch {
        // Sem histórico ou sem permissão: fica a sugestão do custo.
      }
    }
    // O preço só é sugerido quando a pessoa ainda não digitou um.
    aoMudar((ls) => ls.map((l) => (l.chave === chave && l.produto_id === id ? { ...l, preco: l.preco ?? (preco > 0 ? preco : null), dica } : l)));
  }

  return (
    <fieldset className="flex min-w-0 flex-col gap-3 sm:col-span-6">
      <legend className="mb-1 text-sm font-medium text-tinta-2">{t('itens.titulo')}</legend>
      {linhas.map((l, n) => (
        <div key={l.chave} className="grid min-w-0 grid-cols-6 gap-2 rounded-lg border border-borda bg-sup-1 p-3">
          <div className="col-span-6">
            {produtosFixos ? (
              <div className="flex min-w-0 flex-col gap-1.5">
                <span className="text-sm font-medium text-tinta-2">{`${t('itens.produto')} ${n + 1}`}</span>
                <span className="truncate text-sm text-tinta-1" title={l.produto_rotulo}>{l.produto_rotulo}</span>
              </div>
            ) : (
              <Rotulo id={`item-prod-${l.chave}`} rotulo={`${t('itens.produto')} ${n + 1}`}>
                <BuscaRemota
                  id={`item-prod-${l.chave}`}
                  def={{ caminho: '/estoque/produtos', params: { ativo: true }, rotulo: (i) => `${String(i.codigo)} ${String(i.nome)}` }}
                  valor={l.produto_id || null}
                  rotuloAtual={l.produto_rotulo}
                  aoMudar={(v, item) => void escolherProduto(l.chave, v, item)}
                />
              </Rotulo>
            )}
          </div>
          <div className={comPreco ? 'col-span-3 sm:col-span-2' : 'col-span-5'}>
            <Rotulo id={`item-qtd-${l.chave}`} rotulo={t('itens.quantidade')}>
              <Entrada id={`item-qtd-${l.chave}`} inputMode="decimal" className="numero text-right" value={l.quantidade} onChange={(e) => muda(l.chave, { quantidade: e.target.value })} />
            </Rotulo>
          </div>
          {comPreco && (
            <div className="col-span-3 sm:col-span-2">
              <CampoControlado def={{ nome: `preco-${l.chave}`, rotulo: t('itens.preco'), tipo: 'dinheiro' }} valor={l.preco} aoMudar={(v) => muda(l.chave, { preco: v as number | null })} />
            </div>
          )}
          {comPrazo && (
            <div className="col-span-3 sm:col-span-2">
              <Rotulo id={`item-prazo-${l.chave}`} rotulo={t('itens.prazo')}>
                <Entrada id={`item-prazo-${l.chave}`} inputMode="numeric" className="numero text-right" value={l.prazo} onChange={(e) => muda(l.chave, { prazo: e.target.value.replace(/\D/g, '').slice(0, 3) })} />
              </Rotulo>
            </div>
          )}
          {comPreco && (
            <div className="col-span-5 flex min-w-0 flex-col justify-end gap-1 sm:col-span-5">
              <span className="text-sm font-medium text-tinta-2">{t('itens.total')}</span>
              <span className="numero flex min-h-10 flex-wrap items-center gap-x-3 text-sm text-tinta-1">
                {anunciarOcultos(dinheiro(totalDaLinha(l), moeda))}
                {l.dica && <span className="text-xs text-tinta-3">{l.dica}</span>}
              </span>
            </div>
          )}
          <div className="col-span-1 flex items-end justify-end">
            <Botao variante="fantasma" aria-label={t('itens.remover')} title={t('itens.remover')} disabled={linhas.length === 1} onClick={() => aoMudar((ls) => ls.filter((x) => x.chave !== l.chave))} icone={<Trash2 className="size-4" />} />
          </div>
        </div>
      ))}
      <div className="flex flex-wrap items-center justify-between gap-2">
        {!produtosFixos ? (
          <Botao icone={<Plus className="size-4" />} onClick={() => aoMudar((ls) => [...ls, novaLinha()])}>
            {t('itens.adicionar')}
          </Botao>
        ) : (
          <span />
        )}
        {comPreco && (
          <p className="numero text-sm text-tinta-2">
            {t('itens.soma')}: <span className="font-semibold text-tinta-1">{anunciarOcultos(dinheiro(totalDasLinhas(linhas), moeda))}</span>
          </p>
        )}
      </div>
    </fieldset>
  );
}
